import { JSX } from "react";

type CircleProgressIndicatorProps<T> = {
  progress: number; // Progress should be a value between 0 and 100
  size?: number; // Size of the circle (default is 22)
  strokeWidth?: number; // Width of the stroke (default is 4)
  bgColour?: string; // Background circle colour (default is gray)
  fgColour?: string; // Default foreground circle colour (default is black)
  tenderDecision?: T;
  colourMapping?: Record<string, string>; // Maps decision values to colours
};

/**
 * CircleProgressIndicator component renders a circular progress indicator with a cutout effect.
 * @template T - The type of the tender decision.
 * @param {CircleProgressIndicatorProps<T> } props - The props for the component.
 * @returns {JSX.Element} The rendered SVG element representing the circular progress indicator.
 */
const CircleProgressIndicator = <T,>({
  progress,
  size = 22,
  strokeWidth = 4,
  bgColour = "#e6e6e6",
  fgColour = "#000",
  tenderDecision,
  colourMapping,
}: CircleProgressIndicatorProps<T>): JSX.Element => {
  // Clamp progress between 0 and 100
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  // Calculate radius, circumference, and offset
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clampedProgress / 100) * circumference;

  // Default colour mapping
  const defaultColourMapping: Record<string, string> = {
    Approve: "#84CC16", // Green
    Reject: "#EF4444",  // Red
    Bid: "#84CC16",     // Green
    Nobid: "#EF4444",   // Red
  };

  // Determine stroke colour based on progress and tenderDecision
  let strokeColour = fgColour;
  if (clampedProgress === 100 && tenderDecision) {
    const decisionKey = String(tenderDecision);
    strokeColour = colourMapping?.[decisionKey] || defaultColourMapping[decisionKey] || fgColour;
  }

  return (
    <svg width={size} height={size} style={{ width: "100%", height: "100%" }}>
      {/* Background Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={bgColour}
        strokeWidth={strokeWidth}
      />
      {/* Foreground Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={strokeColour}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

export default CircleProgressIndicator;
